import {formatTime, useAsyncEffect, mergeData, str2arr, stringAvatar} from "@/helper/method";
import {TaskBoardBO, TaskBoardVO} from "@/service/task.s";
import React, {useMemo, useState} from "react";
import {useAllDict} from "@/store/dict.c";
import {TaskPO} from "@/model/mission";
import {Avatar, AvatarGroup, Box, Grid, Card, CircularProgress, LinearProgress, Paper, Typography} from "@mui/material";
import Stack from "@mui/material/Stack";
import {MoreVertOutlined, QueryBuilderOutlined} from "@mui/icons-material";
import CardContent from "@mui/material/CardContent";
import {DictPO} from "@/model/system";
import Chip from "@mui/material/Chip";
import {AddOutlined} from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroll-component";
import {styled} from "@mui/material/styles";
import {drawerClasses} from "@mui/material/Drawer";
import {useQueryTaskBoard} from "@/service/task.s";

const taskBoardColors = ["#e7e2f3", "#e1f5fa", "#fff4e5", "#d2f9f4", "#eaeff4"]


const ScrollBoardWrapper = styled(Box)(() => ({
    paddingRight: '5px',
    maxHeight: "calc(100vh - 266px)",
    display: "flex",
    flexDirection: "column",
    minHeight: 100,
    overflowY: "scroll",
    "::-webkit-scrollbar": {
        width: 7,
        height: 7,
    },
    "::-webkit-scrollbar-thumb": {
        [`& .${drawerClasses.paper}`]: {
            backgroundColor: "background.paper",
        },
        cursor: "pointer",
        borderRadius: "3px",
    },
    "&:hover::-webkit-scrollbar-thumb": {
        backgroundColor: "rgba(231,231,226)",
    },
}));

export interface TaskBoardProps {
    onSearch: (query: string) => void;
}

export const TaskBoard = () => {
    const {mutateAsync: queryTaskBoardMutate} = useQueryTaskBoard()
    const [taskBoardMap, setTaskBoardMap] = useState<TaskBoardVO>({})
    const {taskStatus} = useAllDict()

    useAsyncEffect(async () => {
        await loadData({current: 1, statusId: 0})
    }, [])

    const loadData = async (params: Omit<TaskBoardBO, "size">) => {
        const result = await queryTaskBoardMutate({...params, size: 10})
        if (result.code == 0) {
            const statusId = params.statusId
            if (statusId > 0) {
                setTaskBoardMap({
                    ...taskBoardMap,
                    [statusId]: mergeData<TaskPO>(
                        taskBoardMap[statusId]?.list || [],
                        result.data![statusId],
                    )
                })
            } else {
                setTaskBoardMap(result.data!)
            }
        }
    }

    const DraggableCard = ({taskBoard, index}: { taskBoard: TaskPO, index: number }) => {
        const newExecutors: string[] = str2arr(taskBoard.executors, 'string')

        return (
            <Card key={index} variant={'outlined'} sx={{
                border: 'none',
                borderRadius: '1rem',
                padding: "12px 14px",
                marginBottom: "12px",
                cursor: "pointer"
            }}>
                <Stack mb={1} direction={'row'} justifyContent="space-between" alignItems="center">
                    <Typography variant='subtitle1' textOverflow={'ellipsis'} overflow={'hidden'} fontWeight={600} whiteSpace={'nowrap'}
                                fontSize={12}>{taskBoard.taskName}</Typography>
                    <MoreVertOutlined fontSize={'small'} color={'action'}/>
                    {/*<Chip*/}
                    {/*    size="small"*/}
                    {/*    label={task.priority}*/}
                    {/*    color={task.priority === "high" ? "error" : "default"}*/}
                    {/*/>*/}
                </Stack>
                <Stack direction={'row'} spacing={1} mb={1}>
                    <Chip size={'small'} variant={'outlined'} color={'warning'} label={taskBoard.priority}/>
                    <Chip size={'small'} variant={'outlined'} color={'success'} label={taskBoard.virtue}/>
                </Stack>
                <Typography variant="body2" color="text.secondary" fontSize={11} mb={2}>
                    {taskBoard.description}
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <AvatarGroup sx={{
                        '& .MuiAvatarGroup-avatar': {
                            width: 22, height: 22, fontSize: 7, border: 'none'
                        }
                    }} max={3}>
                        {newExecutors?.map((owner: string, index: number) => <Avatar key={index} {...stringAvatar(owner)} />)}
                    </AvatarGroup>
                    {/*<Stack alignItems={'center'} columnGap={1} direction={'row'}>*/}
                    {/*    <Avatar sx={{width: 24, height: 24}} src={taskBoard.executors}/>*/}
                    {/*    <Typography variant={'subtitle2'} color={'textSecondary'}>{taskBoard.executors}</Typography>*/}
                    {/*</Stack>*/}
                </Box>
                <Box mt={1}>
                    <LinearProgress sx={{height: 4}} variant="determinate" value={taskBoard.ratio}/>
                </Box>
                <Stack mt={1} direction={'row'} alignContent={'center'} alignItems={'center'} spacing={0.3}>
                    <QueryBuilderOutlined fontSize={'inherit'} color={'disabled'}/> <Stack direction={'row'} alignContent={'center'} spacing={0.2} alignItems={'center'}>
                    <Typography variant={'caption'} color={'textSecondary'}>
                        {formatTime("D", taskBoard.startTime)} 至 {formatTime("D", taskBoard.endTime)}
                    </Typography>
                </Stack>
                </Stack>
            </Card>
        );
    };

    // const DroppableColumn = ({columnIndex, statusId}: { columnIndex: number, statusId: number }) => {
    //     const currentTaskBoard = taskBoardMap[statusId]
    //     const hasMore = currentTaskBoard?.current < Math.ceil(currentTaskBoard?.total / currentTaskBoard?.size);
    //     return (<ScrollBoardWrapper id={`scrollable${columnIndex}`} key={columnIndex}>
    //             <InfiniteScroll dataLength={currentTaskBoard?.total || 0} next={() => {
    //                 loadData({current: currentTaskBoard?.current + 1, statusId})
    //             }} hasMore={hasMore} scrollableTarget={`scrollable${columnIndex}`} loader={<Box>加载中</Box>}>
    //                 {
    //                     currentTaskBoard?.list?.map((taskBoard: TaskPO, index: number) => (
    //                         <Box style={{height: 100}}>{taskBoard.taskName} </Box>
    //                     ))
    //                 }
    //             </InfiniteScroll>
    //         </ScrollBoardWrapper>
    //     );
    // };

    const taskColumn = useMemo(() => {
        return taskStatus?.sort((a, b) => a.sort! - b.sort!)
    }, [taskStatus]);

    return <CardContent>
        <Grid container columns={20} spacing={3}>
            {taskColumn.map((dict: DictPO, index: number) => {
                const currentTaskBoard = taskBoardMap[dict.value]
                const hasMore = currentTaskBoard?.current < Math.ceil(currentTaskBoard?.total / currentTaskBoard?.size);
                return (
                    <Grid size={4} key={index}>
                        <Paper sx={{py: 2, pl: '12px', backgroundColor: taskBoardColors[index], borderRadius: '1.2rem'}}>
                            <Stack pr={'12px'} direction={'row'} mb={2} justifyContent={'space-between'}>
                                <Stack spacing={1} direction={'row'}>
                                    <Typography variant="h6" fontSize={14}>{dict.label}</Typography>
                                    <Chip color={'error'} variant={'outlined'} label={taskBoardMap[dict.value]?.total || 0}/>
                                </Stack>
                                <AddOutlined fontSize={'small'} sx={{bgcolor: '#fff', borderRadius: '50%'}} color={'action'}/>
                            </Stack>
                            <ScrollBoardWrapper id={`scrollable${index}`} key={index}>
                                <InfiniteScroll dataLength={currentTaskBoard?.total || 0} next={async () => {
                                    await loadData({current: currentTaskBoard?.current + 1, statusId: dict.value})
                                }} hasMore={hasMore} scrollableTarget={`scrollable${index}`}
                                                loader={<Box textAlign={'center'}><CircularProgress size={'20px'} color='secondary'/></Box>}>
                                    {
                                        currentTaskBoard?.list?.map((taskBoard: TaskPO, index: number) => (
                                            <DraggableCard taskBoard={taskBoard} index={index} key={taskBoard.id}/>
                                        ))
                                    }
                                </InfiniteScroll>
                            </ScrollBoardWrapper>
                        </Paper>
                    </Grid>
                )
            })}
        </Grid>
    </CardContent>
}